"use client";
import { Col, Row } from "@/utils/bootstrap";
import { useCurrencyCalc } from "@/hooks/useCurrencyCalc";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { ISupportedCurrency } from "@/api/AppApi/services/company/interfaces/ICompany";
import useIsClient from "@/hooks/useIsClient";
import { useTranslation } from "@/hooks/localization/useTranslations";

interface Props {
  priceWithoutDiscount?: {
    value: number;
    className: string;
    currSymbol?: boolean;
  };
  price: { value: number; className?: string };
  currencySymboleClassName?: string;
  priceFirst?: boolean;
  gap?: number;
  sameLine?: boolean;
  forcedCurrency?: ISupportedCurrency;
  roundPrice?: boolean;
  discountAmount?: number;
  badgeFirst?: boolean;
}

const Price: React.FC<Props> = ({
  priceWithoutDiscount,
  price,
  currencySymboleClassName,
  gap = 4,
  priceFirst = false,
  sameLine = true,
  forcedCurrency,
  roundPrice,
  discountAmount,
  badgeFirst,
}) => {
  const t = useTranslation();
  const isClient = useIsClient();
  const { data } = useGetCompanyQuery();
  const { currency, getPriceString } = useCurrencyCalc({
    enable: data?.result?.theme?.settings?.enable_currencies_converter ?? false,
  });

  const { show_discount_badge } =
    data?.result?.theme?.components?.product ?? {};
  const showBadges = show_discount_badge && discountAmount;

  const Currency = forcedCurrency ?? currency;
  const isDiscount =
    priceWithoutDiscount && +price.value !== +priceWithoutDiscount.value;
  const currencyClassName = currencySymboleClassName ?? price.className;
  if (!isClient) return;
  return (
    <>
      {badgeFirst && !!showBadges && (
        <div
          className="col-auto sale order-3 d-flex gap-1 px-2"
          style={{ width: "fit-content" }}
        >
          {t.save}
          <Price price={{ value: discountAmount, className: "" }} roundPrice />
        </div>
      )}
      <Row
        className={`m-0 align-items-center ${sameLine ? "flex-nowrap" : ""}`}
        style={{ gap: gap, width: "fit-content" }}
      >
        {!badgeFirst && !!showBadges && (
          <Col className="col-auto sale order-3 d-flex gap-1">
            {t.save_discount}
            <Price
              price={{ value: discountAmount, className: "" }}
              roundPrice
            />
          </Col>
        )}
        {!!isDiscount && (
          <Col
            xs={
              priceFirst
                ? { order: 1, span: sameLine ? "auto" : 12 }
                : sameLine
                ? "auto"
                : 12
            }
            className={`p-0 d-flex ${priceWithoutDiscount.className}`}
            style={{ gap: 4, width: "fit-content" }}
          >
            {getPriceString(
              +priceWithoutDiscount.value,
              forcedCurrency,
              roundPrice,
            )}
            {priceWithoutDiscount.currSymbol && <span>{Currency?.symbol}</span>}
          </Col>
        )}
        <Col
          xs={sameLine ? "auto" : 12}
          className="p-0 d-flex col-auto align-items-center"
          style={{ gap: 4, width: "fit-content" }}
        >
          <span className={price.className}>
            {getPriceString(+price.value, forcedCurrency, roundPrice)}
          </span>
          <span className={currencyClassName}>{Currency?.symbol}</span>
        </Col>
      </Row>
    </>
  );
};

export default Price;
